import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  IndianRupee,
  BarChart3,
  FolderOpen,
  Building2,
  LogOut,
  Menu,
  X,
  Lock,
  Settings,
  Shield,
  ShieldOff,
  CalendarDays,
  Mail,
  FileCode2,
  ClipboardList,
  Calculator,
  Receipt,
  BarChart2,
  UserMinus,
  PieChart,
  Download,
  BookOpen,
  LogOutIcon,
  TrendingUp,
  ScrollText,
  Award,
  FileStack,
  Wallet,
  UserCog,
  Database,
  Package,
  Network,
  User,
  ChevronDown,
  Activity,
  Sliders,
  Heart,
  Crosshair,
  UserPlus,
  UserCheck,
  GraduationCap,
  Target,
  Send,
  Boxes,
  Store,
  Truck,
  PackageCheck,
  History,
  QrCode,
  ShoppingCart,
  Warehouse as WarehouseIcon,
  ChevronRight,
  Home as HomeIcon,
  Briefcase,
  ShieldCheck,
  AlertTriangle,
  Trash2,
  KeyRound
} from 'lucide-react';
import { Button } from './ui/button';
import api, { getCompanySettings } from '../api';
import NotificationBell from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Layout = ({ children, user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [companyInfo, setCompanyInfo] = React.useState(null);
  const [allowedSections, setAllowedSections] = React.useState(null);

  React.useEffect(() => {
    fetchCompanyInfo();
    fetchMyPermissions();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await getCompanySettings();
      setCompanyInfo(response.data);
    } catch (error) {
      // Company info not set, use defaults
    }
  };

  const fetchMyPermissions = async () => {
    try {
      const res = await api.get('/permissions/my-sections');
      setAllowedSections(res.data.sections);
    } catch {
      // Fallback: role-based filtering only
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const navGroups = [
    {
      id: 'home', label: 'Overview', icon: HomeIcon, items: [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'hr_manager', 'employee', 'ops_manager'] },
        { path: '/ops-dashboard', icon: Crosshair, label: 'Ops Dashboard', roles: ['admin', 'ops_manager'] },
        { path: '/hr-analytics', icon: TrendingUp, label: 'HR Analytics', roles: ['admin', 'hr_manager'] },
        { path: '/bgv', icon: ShieldCheck, label: 'Background Verification', roles: ['admin', 'hr_manager', 'hr_executive'] },
      ]
    },
    {
      id: 'people', label: 'People', icon: Users, items: [
        { path: '/employees', icon: Users, label: 'Employees', roles: ['admin', 'hr_manager'] },
        { path: '/employees/duplicate-cleanup', icon: AlertTriangle, label: 'Duplicate Cleanup', roles: ['admin', 'hr_manager'] },
        { path: '/employees/deleted', icon: Trash2, label: 'Deleted / Restore', roles: ['admin', 'hr_manager'] },
        { path: '/org-chart', icon: Network, label: 'Org Chart', roles: ['admin', 'hr_manager', 'employee'] },
        { path: '/departments', icon: Building2, label: 'Departments', roles: ['admin', 'hr_manager'] },
        { path: '/onboarding-admin', icon: UserPlus, label: 'Onboarding', roles: ['admin', 'hr_manager'] },
        { path: '/probation-tracker', icon: UserCheck, label: 'Probation Tracker', roles: ['admin', 'hr_manager'] },
      ]
    },
    {
      id: 'attendance', label: 'Time & Leave', icon: Calendar, items: [
        { path: '/attendance', icon: Calendar, label: 'Attendance', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/attendance-summary', icon: BarChart2, label: 'Attendance Summary', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/attendance-edit-logs', icon: ClipboardList, label: 'Attendance Logs', roles: ['admin', 'hr_executive'] },
        { path: '/leaves', icon: FileText, label: 'Leaves', roles: ['admin', 'hr_manager', 'employee', 'ops_manager'] },
        { path: '/leave-policy', icon: BookOpen, label: 'Leave Policy', roles: ['admin', 'hr_manager'] },
        { path: '/leave-conflict-settings', icon: Sliders, label: 'Leave Conflict', roles: ['admin', 'hr_manager'] },
      ]
    },
    {
      id: 'payroll', label: 'Payroll', icon: IndianRupee, items: [
        { path: '/payroll', icon: IndianRupee, label: 'Payroll', roles: ['admin', 'hr_manager', 'accounts_manager'] },
        { path: '/ppu-salary', icon: Calculator, label: 'PPU Salary', roles: ['admin', 'hr_manager', 'accounts_manager'] },
        { path: '/payslips', icon: IndianRupee, label: 'Payslips', roles: ['admin', 'hr_manager', 'accounts_manager', 'employee', 'ops_manager'] },
        { path: '/tax-declaration', icon: Calculator, label: 'Tax Declaration', roles: ['admin', 'hr_manager', 'accounts_manager', 'employee'] },
        { path: '/reimbursements', icon: Receipt, label: 'Reimbursements', roles: ['admin', 'hr_manager', 'accounts_manager', 'employee'] },
        { path: '/statutory-compliance', icon: Shield, label: 'Compliance', roles: ['admin', 'hr_manager', 'accounts_manager'] },
      ]
    },
    {
      id: 'performance', label: 'Performance', icon: Activity, items: [
        { path: '/reviews', icon: BarChart3, label: 'Reviews', roles: ['admin', 'hr_manager', 'employee'] },
        { path: '/my-performance', icon: Activity, label: 'My Performance', roles: ['admin', 'hr_manager', 'hr_executive', 'employee', 'manager', 'accounts_manager', 'ops_manager'] },
        { path: '/kudos', icon: Heart, label: 'Kudos', roles: ['admin', 'hr_manager', 'hr_executive', 'employee', 'manager', 'accounts_manager', 'ops_manager'] },
        { path: '/skills-matrix', icon: BarChart3, label: 'Skills Matrix', roles: ['admin', 'hr_manager'] },
        { path: '/training', icon: GraduationCap, label: 'Training', roles: ['admin', 'hr_manager'] },
        { path: '/my-training', icon: BookOpen, label: 'My Training', roles: ['admin', 'hr_manager', 'employee', 'ops_manager'] },
      ]
    },
    {
      id: 'recruitment', label: 'Recruitment', icon: Briefcase, items: [
        { path: '/offer-letters', icon: Mail, label: 'Offer Letters', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/offer-letters-summary', icon: ClipboardList, label: 'Offer Summary', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/offer-letter-templates', icon: FileCode2, label: 'Letter Templates', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/recruiter-performance', icon: Target, label: 'Recruiter Performance', roles: ['admin', 'hr_manager', 'employee', 'ops_manager'] },
        { path: '/employee-letters', icon: Award, label: 'Employee Letters', roles: ['admin', 'hr_manager'] },
        { path: '/kyc-verification', icon: ShieldCheck, label: 'eKYC Verification', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/bgv', icon: ShieldCheck, label: 'Background Verification', roles: ['admin', 'hr_manager', 'hr_executive'] },
      ]
    },
    {
      id: 'documents', label: 'Documents', icon: FolderOpen, items: [
        { path: '/documents', icon: FolderOpen, label: 'Documents', roles: ['admin', 'hr_manager', 'hr_executive', 'employee'] },
        { path: '/documents-consolidated', icon: FileStack, label: 'Doc Overview', roles: ['admin', 'hr_manager', 'hr_executive'] },
      ]
    },
    {
      id: 'assets', label: 'Assets', icon: Package, items: [
        { path: '/asset-management', icon: Package, label: 'Asset Management', roles: ['admin', 'hr_manager', 'hr_executive'] },
        { path: '/my-assets', icon: Package, label: 'My Assets', roles: ['employee'] },
      ]
    },
    {
      id: 'inventory', label: 'Inventory', icon: Boxes, items: [
        { path: '/inventory', icon: Boxes, label: 'Inventory Dashboard', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/items', icon: QrCode, label: 'Inventory Items', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/merchants', icon: Store, label: 'Merchants', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/transfer', icon: Truck, label: 'Transfer / Issue', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/requests', icon: ShoppingCart, label: 'Requests', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/returns', icon: PackageCheck, label: 'Returns', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/warehouses', icon: WarehouseIcon, label: 'Warehouses & Catalog', roles: ['admin', 'hr_manager', 'warehouse_manager'] },
        { path: '/inventory/movements', icon: History, label: 'Movement History', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/reports', icon: BarChart3, label: 'Reports', roles: ['admin', 'hr_manager', 'warehouse_manager', 'state_head', 'asm', 'tl', 'fse'] },
        { path: '/inventory/analytics', icon: BarChart3, label: 'Distribution Analytics', roles: ['admin', 'hr_manager', 'warehouse_manager'] },
        { path: '/inventory/users', icon: Users, label: 'Users & Access', roles: ['admin', 'hr_manager', 'warehouse_manager'] },
      ]
    },
    {
      id: 'accounting', label: 'Accounting', icon: PieChart, items: [
        { path: '/accounting', icon: PieChart, label: 'Accounting', roles: ['admin', 'accounts_manager'] },
        { path: '/accounting/receivables', icon: Wallet, label: 'Receivables', roles: ['admin', 'accounts_manager'] },
      ]
    },
    {
      id: 'exit', label: 'Exit & Offboarding', icon: LogOutIcon, items: [
        { path: '/exit-management', icon: LogOutIcon, label: 'Exit Management', roles: ['admin', 'hr_manager', 'accounts_manager'] },
        { path: '/final-settlement', icon: UserMinus, label: 'Final Settlement', roles: ['admin', 'hr_manager', 'accounts_manager'] },
        { path: '/access-removal', icon: ShieldOff, label: 'Access Removal', roles: ['admin'] },
      ]
    },
    {
      id: 'admin', label: 'Admin', icon: Settings, items: [
        { path: '/user-management', icon: UserCog, label: 'User Management', roles: ['admin'] },
        { path: '/permissions', icon: Shield, label: 'Permissions', roles: ['admin'] },
        { path: '/company-settings', icon: Settings, label: 'Company Settings', roles: ['admin'] },
        { path: '/integrations-settings', icon: KeyRound, label: 'Integrations', roles: ['admin'] },
        { path: '/data-export', icon: Download, label: 'Data Export', roles: ['admin'] },
        { path: '/login-audit-log', icon: ScrollText, label: 'Login Audit', roles: ['admin'] },
        { path: '/email-logs', icon: Send, label: 'Email Logs', roles: ['admin'] },
        { path: '/health-monitor', icon: Activity, label: 'API Health', roles: ['admin'] },
      ]
    },
  ];

  // Filter items per group
  const filterItem = (item) => {
    if (allowedSections) return allowedSections.includes(item.path);
    return item.roles.includes(user?.role);
  };

  const visibleGroups = navGroups
    .map(g => ({ ...g, items: g.items.filter(filterItem) }))
    .filter(g => g.items.length > 0);

  // Track which groups are open. Auto-open the group containing the active route.
  const findActiveGroup = () => {
    for (const g of visibleGroups) {
      if (g.items.some(i => i.path === location.pathname)) return g.id;
    }
    return null;
  };

  const [openGroups, setOpenGroups] = React.useState(() => {
    try {
      const stored = localStorage.getItem('sidebar_open_groups');
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  });

  React.useEffect(() => {
    const activeGroup = findActiveGroup();
    if (activeGroup && !openGroups[activeGroup]) {
      setOpenGroups(prev => ({ ...prev, [activeGroup]: true }));
    }
    // eslint-disable-next-line
  }, [location.pathname, allowedSections]);

  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('sidebar_open_groups', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  return (
    <div className="app-container">
      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              {companyInfo?.logo_url && (
                <img 
                  src={companyInfo.logo_url} 
                  alt="Company Logo" 
                  className="h-8 w-8 object-contain flex-shrink-0"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <h1 className="text-base font-bold text-indigo-600 truncate" data-testid="app-logo">
                {companyInfo?.company_name || 'T&N Business Services'}
              </h1>
            </div>
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="close-mobile-menu-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <nav className="p-2 overflow-y-auto flex-1 pb-2" data-testid="sidebar-nav">
          {visibleGroups.map((group) => {
            const GroupIcon = group.icon;
            const isOpen = !!openGroups[group.id];
            const hasActiveChild = group.items.some(i => i.path === location.pathname);
            return (
              <div key={group.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`nav-group-${group.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide transition-colors ${
                    hasActiveChild ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <GroupIcon size={14} strokeWidth={2} />
                    {group.label}
                  </span>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isOpen && (
                  <div className="mt-0.5 ml-2 pl-2 border-l border-slate-100 space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors text-[13px] ${
                            isActive
                              ? 'bg-indigo-50 text-indigo-700 font-medium'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Icon size={15} strokeWidth={1.75} className="flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-2 border-t border-slate-100 flex-shrink-0">
          <div className="px-2.5 py-1.5 mb-1.5 bg-slate-50 rounded-md">
            <p className="text-xs font-medium text-slate-900 truncate" data-testid="user-name">{user?.full_name}</p>
            <p className="text-[11px] text-slate-500 capitalize" data-testid="user-role">{user?.role?.replace('_', ' ')}</p>
          </div>
          <Link
            to="/change-password"
            data-testid="change-password-link"
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-[13px]"
          >
            <Lock size={15} />
            <span>Change Password</span>
          </Link>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50 text-[13px] h-8 px-2.5"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={15} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <header className="sticky top-0 z-10 glass">
          <div className="flex items-center justify-between px-8 py-4">
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="open-mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <div className="hidden md:block">
              <h2 className="text-lg font-semibold text-slate-900">Welcome back, {user?.full_name}!</h2>
              <p className="text-sm text-slate-500">Manage your workforce efficiently</p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3 py-2 h-auto" data-testid="profile-dropdown">
                    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-semibold text-sm">
                      {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                      <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                    </div>
                    <ChevronDown size={16} className="text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/my-profile" className="flex items-center gap-2 cursor-pointer" data-testid="my-profile-link">
                      <User size={16} />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/change-password" className="flex items-center gap-2 cursor-pointer">
                      <Lock size={16} />
                      Change Password
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600 cursor-pointer"
                    data-testid="dropdown-logout"
                  >
                    <LogOut size={16} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;